import Enumerable from "linq";
import { DataGridColumn, RowItem } from "../components/comp-data-grid/comp-data-grid.models";
import { QueryFilter, DataType } from "../models/queries.models";
import { StringHelper } from "./string.helper";
import { DateTimeHelper } from "./date-time.helper";

export class QueryFilterHelper {

    public static filterCollection(itemsCollection: RowItem[], columns: DataGridColumn[]): RowItem[] {
        let filtersToInclude: QueryFilter[] = [];
        columns.forEach(x => {
            if (x.filtersResolver) {
                const filters = x.filtersResolver(x.filters);
                if (filters) {
                    filtersToInclude = filtersToInclude.concat(filters);
                }
            } else {
                if (x.filters) {
                    filtersToInclude = filtersToInclude.concat(x.filters);
                }
            }
        });
        itemsCollection = itemsCollection && filtersToInclude && filtersToInclude.length > 0
            ? itemsCollection.filter(rowItem => QueryFilterHelper.areConditionsSatisfied(rowItem, rowItem.item, filtersToInclude))
            : itemsCollection;
        return itemsCollection;
    }

    private static areConditionsSatisfied(rowItem: RowItem, item: any, filters: QueryFilter[]): boolean {
        const groupedFilters = Enumerable.from(filters).groupBy(x => x.groupGuid);
        let response: boolean = true;
        groupedFilters.forEach(filterGroup => {
            let groupResponse: boolean = true;
            filterGroup.forEach(filter => {
                if (filter.isAnd) {
                    groupResponse = groupResponse && QueryFilterHelper.propertyComparison(rowItem, item, filter);
                }
                else {
                    groupResponse = groupResponse || QueryFilterHelper.propertyComparison(rowItem, item, filter);
                }
            });
            response = response && groupResponse;
        });
        return response;
    }

    private static unformat(value, decimal) {

        // Fails silently (need decent errors):
        value = value || 0;

        // Return the value as-is if it's already a number:
        if (typeof value === "number") return value;

        // Default decimal point comes from settings, but could be set to eg. "," in opts:
        decimal = decimal || ".";

        // Build regex to strip out everything except digits, decimal point and minus sign:
        const regex = new RegExp("[^0-9-" + decimal + "]", "g"),
            unformatted = parseFloat(
                ("" + value)
                    .replace(/\((?=\d+)(.*)\)/, "-$1") // replace bracketed values with negatives
                    .replace(regex, '')         // strip out any cruft
                    .replace(decimal, '.')      // make sure decimal point is standard
            );

        // This will fail silently which may cause trouble, let's wait and see:
        return !isNaN(unformatted) ? unformatted : 0;
    }

    private static propertyComparison(rowItem: RowItem, item: any, filter: QueryFilter): boolean {
        const operation = filter.filterOperator;
        const valueConverter = rowItem.columnKeys[filter.filterProperty.name] && rowItem.columnKeys[filter.filterProperty.name].column
            ? rowItem.columnKeys[filter.filterProperty.name].column.valueConverter
            : null;

        const propertyValueFromItem = this.getPropertyValue(item, filter.filterProperty.name);

        let propertyValue: string | number | Date = valueConverter ? valueConverter(propertyValueFromItem, item) : propertyValueFromItem;
        let filterValue: string | number | Date = filter.filterValue;
        //Here we convert all numbers to double to be able to compare numbers (e. int cannot be compared to long)
        switch (filter.filterProperty.type) {
            case DataType.Number:
            case DataType.Double:
                propertyValue = propertyValue == null ? null : this.unformat(propertyValue.toString(), null);
                filterValue = filter.filterValue !== null && this.isNumeric(filter.filterValue) ? this.unformat(filter.filterValue, null) : null;
                break;
            case DataType.Date:
            case DataType.DateTime:
                if (!(propertyValue instanceof Date) && typeof propertyValue == "string") {
                    propertyValue = new Date(propertyValue);
                }
                if (!(filterValue instanceof Date) && typeof filterValue == "string") {
                    filterValue = DateTimeHelper.castDate(filterValue);
                }
                if (filterValue instanceof Date) {
                    filterValue = DateTimeHelper.castDate(filterValue).getTime();
                }
                if (propertyValue instanceof Date) {
                    propertyValue = DateTimeHelper.castDate(propertyValue).getTime();
                }
                break;
            case DataType.Default:
                break;
        }

        if (((filterValue == null || filterValue.toString() == "") && filter.skipOnNullOrEmpty) || (filterValue === undefined && filter.filterProperty.type == DataType.Boolean)) {
            return true;
        }

        if (propertyValue && typeof propertyValue == "string") {
            propertyValue = propertyValue.toLowerCase();
        }

        if (typeof filterValue == "string") {
            filterValue = filterValue.toLowerCase();
        }
        switch (operation.translatedSymbol) {
            case "=":
            case "eq":
                return (propertyValue == null && propertyValue == filterValue) || (propertyValue != null && (propertyValue == filterValue));
            case "<>":
            case "ne":
                return (propertyValue == null && propertyValue != filterValue) || (propertyValue != null && (propertyValue != filterValue));
            case "<":
            case "lt":
                return propertyValue != null && (propertyValue < filterValue);
            case "<=":
            case "le":
                return propertyValue != null && (propertyValue <= filterValue);
            case ">":
            case "gt":
                return propertyValue != null && propertyValue > filterValue;
            case ">=":
            case "ge":
                return propertyValue != null && propertyValue >= filterValue;
            case "endswith({0}, '{1}')":
                return propertyValue != null && StringHelper.endsWith(propertyValue.toString(), filterValue.toString());
            case "startswith({0}, '{1}')":
                return propertyValue != null && StringHelper.startsWith(propertyValue.toString(), filterValue.toString());
            case "contains({0}, '{1}')":
                return propertyValue != null && propertyValue.toString().indexOf(filterValue.toString()) > -1;
            default:
                return true;
        }
    }

    private static isNumeric(str: string | number) {
        if (typeof str == "number") {
            return !isNaN(str);
        }
        if (typeof str == "string") {
            return !isNaN(parseFloat(str));
        }
        return false;
    }

    private static getPropertyValue(item: any, property: string): any {
        const props: string[] = property.split(".");
        if (props.length === 1) {
            return item ? item[property] : null;
        }
        const value = QueryFilterHelper.getPropertyValue(item[props[0]], property.substring(property.indexOf(".") + 1));
        return value;
    }
}
